import React, { memo } from 'react';
import { Box, Typography, Button, Stack, alpha } from '@mui/material';
import { EventNote, ArrowForward, BeachAccess } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PendingLeavesData } from '../../types/dashboardTypes';
import {
    AccentAvatar,
    AccentChip,
    DashboardCard,
    DashboardEmptyState,
    DashboardListCardSkeleton,
    DashboardListRow,
    thinScrollbarSx,
    useDashboardAccent,
    useDashboardTones,
} from './DashboardCard';

interface PendingLeavesCardProps {
    data: PendingLeavesData;
}

/**
 * Elegant Pending Leave Requests Card
 * Shows pending leave requests with quick action to approval page
 * Supports light and dark themes with refined aesthetics
 */
const PendingLeavesCard: React.FC<PendingLeavesCardProps> = memo(({ data }) => {
    const navigate = useNavigate();
    const { theme, isDark, ink } = useDashboardTones();
    const amber = useDashboardAccent('warning');
    const { count, requests } = data;

    const formatDateRange = (start: string, end: string) => {
        const startDate = new Date(start);
        const endDate = new Date(end);
        const startDay = startDate.getDate();
        const endDay = endDate.getDate();
        const month = startDate.toLocaleDateString('en-US', { month: 'short' });
        
        if (startDay === endDay) {
            return `${month} ${startDay}`;
        }
        return `${month} ${startDay}-${endDay}`;
    };

    return (
        <DashboardCard
            accent="warning"
            icon={EventNote}
            title="Leave Requests"
            subtitle="Awaiting your approval"
            actions={count > 0 && (
                <AccentChip
                    accent="warning"
                    variant="solid"
                    label={count}
                    sx={{
                        fontWeight: 700,
                        minWidth: 28,
                        height: 28,
                        fontSize: 12,
                        boxShadow: isDark ? 'none' : `0 2px 6px ${alpha(amber.main, 0.3)}`,
                    }}
                />
            )}
            footer={count > 0 && (
                <Button
                    fullWidth
                    variant="contained"
                    endIcon={<ArrowForward sx={{ fontSize: 14 }} />}
                    onClick={() => navigate('/app/leaves/approval')}
                    sx={{
                        bgcolor: isDark ? alpha(amber.main, 0.15) : amber.main,
                        color: isDark ? amber.text : theme.palette.common.white,
                        textTransform: 'none',
                        borderRadius: 1.5,
                        py: 0.75,
                        fontWeight: 500,
                        fontSize: 13,
                        boxShadow: 'none',
                        '&:hover': {
                            bgcolor: isDark ? alpha(amber.main, 0.25) : amber.dark,
                            boxShadow: isDark ? 'none' : `0 2px 8px ${alpha(amber.main, 0.3)}`,
                        },
                    }}
                >
                    Review All Requests
                </Button>
            )}
        >
            {/* Request List */}
            <Box
                sx={{
                    flex: 1,
                    overflow: requests.length > 0 ? 'auto' : 'hidden',
                    px: 1.5,
                    py: 1,
                    display: requests.length === 0 ? 'flex' : 'block',
                    minHeight: requests.length > 0 ? 200 : 'auto',
                    maxHeight: requests.length > 0 ? 260 : 'none',
                    ...thinScrollbarSx(ink(0.1), ink(0.2)),
                }}
            >
                {requests.length === 0 ? (
                    <DashboardEmptyState
                        accent="success"
                        icon={BeachAccess}
                        title="No pending leave requests"
                        description="All caught up!"
                    />
                ) : (
                    <Stack spacing={0.75}>
                        {requests.map((request) => (
                            <DashboardListRow key={request._id} interactive sx={{ gap: 1.5 }}>
                                <AccentAvatar accent="warning">
                                    {request.employeeName.charAt(0).toUpperCase()}
                                </AccentAvatar>
                                <Box flex={1} minWidth={0}>
                                    <Typography variant="body2" fontWeight={500} noWrap color="text.primary" fontSize={13}>
                                        {request.employeeName}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" noWrap fontSize={11}>
                                        {request.leaveType} • {request.totalDays} day{request.totalDays > 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                                <AccentChip
                                    accent="warning"
                                    label={formatDateRange(request.startDate, request.endDate)}
                                    sx={{ height: 20, fontSize: 10, fontWeight: 500 }}
                                />
                            </DashboardListRow>
                        ))}
                    </Stack>
                )}
            </Box>
        </DashboardCard>
    );
});

PendingLeavesCard.displayName = 'PendingLeavesCard';

export const PendingLeavesCardSkeleton: React.FC = memo(() => (
    <DashboardListCardSkeleton subtitle sx={{ height: '100%' }} />
));

PendingLeavesCardSkeleton.displayName = 'PendingLeavesCardSkeleton';

export default PendingLeavesCard;
